import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Plus, Users, Trash2, User, Building, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

interface ManagedUser {
  id: string;
  email: string;
  created_at: string;
  role: 'admin' | 'staff' | null;
  establishment_id: string | null;
  establishment_name: string | null;
  is_super_admin: boolean;
}

interface Establishment {
  id: string;
  name: string;
  city: string | null;
  is_active: boolean;
}

export default function UserManagement() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { role: currentUserRole, session, isAdmin, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  // Form state
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<'admin' | 'staff' | 'super-admin'>('staff');
  const [establishmentId, setEstablishmentId] = useState<string>("");

  // Redirect if not admin
  useEffect(() => {
    if (currentUserRole && !isAdmin) {
      toast({
        title: "Accès refusé",
        description: "Seuls les administrateurs peuvent accéder à cette page.",
        variant: "destructive",
      });
      navigate('/dashboard');
    }
  }, [currentUserRole, isAdmin, navigate, toast]);

  // Fetch establishments for super admins
  useEffect(() => {
    if (isSuperAdmin) {
      supabase
        .from("establishments")
        .select("id, name, city, is_active")
        .order("name")
        .then(({ data, error }) => {
          if (error) {
            console.error("Error fetching establishments:", error);
          }
          setEstablishments(data || []);
        });
    }
  }, [isSuperAdmin]);

  const fetchUsers = async () => {
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke('manage-users', {
        headers: {
          Authorization: `Bearer ${currentSession?.access_token}`
        },
        body: { action: 'list' }
      });

      if (error) throw error;
      setUsers(data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les utilisateurs.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin]);

  const handleCreateUser = async () => {
    if (!email) {
      toast({
        title: "Erreur",
        description: "Email requis.",
        variant: "destructive",
      });
      return;
    }

    if (isSuperAdmin && role !== 'super-admin' && !establishmentId) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner un établissement.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const body: Record<string, string> = {
        action: 'create',
        email,
        role
      };
      if (isSuperAdmin && role !== 'super-admin' && establishmentId) {
        body.establishmentId = establishmentId;
      }

      const { data, error } = await supabase.functions.invoke('manage-users', {
        headers: {
          Authorization: `Bearer ${currentSession?.access_token}`
        },
        body
      });

      if (error) {
        let errorMessage = "Impossible de créer l'utilisateur.";
        try {
          const errorBody = typeof error.context?.body === 'string' 
            ? JSON.parse(error.context.body) 
            : error.context?.body;
          if (errorBody?.error) {
            errorMessage = errorBody.error;
          }
        } catch {
          // fallback
        }
        
        toast({
          title: "Erreur",
          description: errorMessage,
          variant: "destructive",
        });
        return;
      }

      if (data?.error) {
        toast({
          title: "Erreur",
          description: data.error,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Utilisateur créé",
        description: `${email} a été créé. Un email de configuration du mot de passe a été envoyé.`,
      });

      setIsDialogOpen(false);
      setEmail("");
      setRole('staff');
      setEstablishmentId("");
      await fetchUsers();
    } catch (error: any) {
      console.error('Error creating user:', error);
      toast({
        title: "Erreur",
        description: "Impossible de créer l'utilisateur.",
        variant: "destructive",
      });
    }
  };

  const handleUpdateRole = async (userId: string, newRole: 'admin' | 'staff') => {
    const targetUser = users.find(u => u.id === userId);
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const { error } = await supabase.functions.invoke('manage-users', {
        headers: {
          Authorization: `Bearer ${currentSession?.access_token}`
        },
        body: {
          action: 'update-role',
          userId,
          role: newRole,
          establishmentId: targetUser?.establishment_id
        }
      });

      if (error) throw error;

      toast({
        title: "Rôle modifié",
        description: "Le rôle de l'utilisateur a été mis à jour.",
      });

      await fetchUsers();
    } catch (error) {
      console.error('Error updating role:', error);
      toast({
        title: "Erreur",
        description: "Impossible de modifier le rôle.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ?")) return;

    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const { error } = await supabase.functions.invoke('manage-users', {
        headers: {
          Authorization: `Bearer ${currentSession?.access_token}`
        },
        body: {
          action: 'delete',
          userId
        }
      });

      if (error) throw error;

      toast({
        title: "Utilisateur supprimé",
        description: "L'utilisateur a été supprimé avec succès.",
      });

      await fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer l'utilisateur.",
        variant: "destructive",
      });
    }
  };

  if (!isAdmin) {
    return null;
  }

  const orphanCount = users.filter(u => !u.establishment_id && !u.is_super_admin).length;

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" size="sm" className="mb-4" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour au tableau de bord
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-serif font-bold mb-2">Gestion des utilisateurs</h1>
              <p className="text-muted-foreground">
                Gérez les accès administrateurs et staff
              </p>
            </div>
            {isSuperAdmin && (
              <Button onClick={() => setIsDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Nouvel utilisateur
              </Button>
            )}
          </div>
        </div>

        {/* Current User Banner */}
        <Card className="mb-6 bg-primary/5 border-primary/20">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Connecté en tant que</p>
                <p className="font-semibold">{session?.user?.email}</p>
              </div>
              <Badge className="ml-auto" variant="default">
                {currentUserRole}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className={`grid grid-cols-1 gap-4 mb-6 ${isSuperAdmin ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total</CardTitle>
              <Users className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{users.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Administrateurs</CardTitle>
              <Users className="w-4 h-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{users.filter(u => u.role === 'admin').length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Staff</CardTitle>
              <Users className="w-4 h-4 text-secondary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{users.filter(u => u.role === 'staff').length}</div>
            </CardContent>
          </Card>
          {isSuperAdmin && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Sans établissement</CardTitle>
                <AlertCircle className="w-4 h-4 text-warning" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{orphanCount}</div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Users List */}
        <Card>
          <CardHeader>
            <CardTitle>Utilisateurs</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">Chargement...</p>
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">Aucun utilisateur</p>
              </div>
            ) : (
              <div className="space-y-4">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold">{user.email}</p>
                      <div className="flex items-center gap-2 flex-wrap mt-1">
                        <p className="text-sm text-muted-foreground">
                          Créé le {new Date(user.created_at).toLocaleDateString('fr-FR')}
                        </p>
                        {isSuperAdmin && (
                          <>
                            {user.establishment_name ? (
                              <Badge variant="outline" className="text-xs">
                                <Building className="w-3 h-3 mr-1" />
                                {user.establishment_name}
                              </Badge>
                            ) : !user.is_super_admin && (
                              <Badge variant="warning" className="text-xs">
                                Sans établissement
                              </Badge>
                            )}
                            {user.is_super_admin && (
                              <Badge variant="info" className="text-xs">
                                Super Admin
                              </Badge>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {user.is_super_admin ? (
                        <Badge variant="info">Super Admin</Badge>
                      ) : (
                        <>
                          <Select
                            value={user.role || 'staff'}
                            onValueChange={(value: 'admin' | 'staff') => handleUpdateRole(user.id, value)}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="staff">Staff</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                          <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                            {user.role || 'Aucun'}
                          </Badge>
                        </>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteUser(user.id)}
                        disabled={user.id === session?.user?.id}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create User Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nouvel utilisateur</DialogTitle>
              <DialogDescription>
                Créez un nouveau compte. L'utilisateur recevra un email pour configurer son mot de passe.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Rôle</Label>
                <Select value={role} onValueChange={(value: 'admin' | 'staff' | 'super-admin') => setRole(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    {isSuperAdmin && (
                      <SelectItem value="super-admin">Super Admin</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              {isSuperAdmin && role !== 'super-admin' && (
                <div className="space-y-2">
                  <Label htmlFor="establishment">Établissement</Label>
                  <Select value={establishmentId} onValueChange={setEstablishmentId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un établissement" />
                    </SelectTrigger>
                    <SelectContent>
                      {establishments.map((est) => (
                        <SelectItem key={est.id} value={est.id}>
                          {est.name}{est.city ? ` — ${est.city}` : ''}
                          {!est.is_active ? ' (inactif)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {isSuperAdmin && role === 'super-admin' && (
                <p className="text-sm text-muted-foreground">
                  Les super-admins ont accès à tous les établissements.
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleCreateUser}>
                Créer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
